import { BOX_SHADOW_LG, GRAY_1000, GRAY_900, WHITE } from 'constants/styling/theme'
import React, { useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IconButton from '@mui/material/IconButton'

/** @interface Props for the FloatingScrollButton component. */
interface Props {
  /** Optional ID of the parent element to which the button is related. */
  parentId?: string
}

/**
 * A floating button component that appears when the specified parent container is scrollable.
 * When clicked, it smoothly scrolls the container to the bottom.
 *
 * @example
 * <FloatingScrollButton parentId="scrollable-container" />
 */
export const FloatingScrollButton: React.FC<Props> = ({ parentId }) => {
  const [isScrollable, setIsScrollable] = useState(false)
  const [container, setContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    // Delay to ensure DOM is rendered as the paren component uses Fade with 600ms timeout
    const timeout = setTimeout(() => {
      if (parentId) {
        const element = document.getElementById(parentId)
        setContainer(element)

        if (element) setIsScrollable(element.scrollHeight > element.clientHeight)
      }
    }, 600)

    // Check container on window resize
    const handleResize = () => {
      if (parentId) {
        const element = document.getElementById(parentId)
        setContainer(element)
        if (element) {
          setIsScrollable(element.scrollHeight > element.clientHeight)
        }
      }
    }

    window.addEventListener('resize', handleResize)

    // Cleanup function to clear the timeout and remove the event listener
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [parentId])

  // Scroll to the bottom of the container smoothly
  const onScrollDown = () => {
    if (container) {
      const scrollPercentage = 0.4
      const currentScrollPosition = container.scrollTop

      if (currentScrollPosition === container.scrollHeight) return

      container.scrollTo({
        top: currentScrollPosition + (container.scrollHeight * scrollPercentage),
        behavior: 'smooth'
      })
    }
  }

  if (!isScrollable || !parentId) return null

  return (
    <Box
      sx={{
        zIndex: 1200,
        right: '1rem',
        bottom: '2rem',
        position: 'absolute',
      }}
    >
      <IconButton
        aria-label="scroll-down"
        sx={{
          width: '4rem',
          height: '4rem',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '50%',
          justifyContent: 'center',
          backgroundColor: GRAY_900,
          boxShadow: BOX_SHADOW_LG,
          border: `1px solid ${GRAY_1000}`,
          ':hover': {
            backgroundColor: GRAY_900, // keep the same color on hover
          }
        }}
        onClick={onScrollDown}
      >
        <ExpandMoreIcon sx={{ color: WHITE, fontSize: '2.4rem' }} />
      </IconButton>
    </Box>
  )
}
